
import {
  forEachSections,
  sectionsWithDataProps as abSectionsWithDataProps,
} from '@webplatform/asbis-ui/dist/sections';
import { defineComponent } from '@nuxtjs/composition-api';
import type {
  PreparedBlock,
  PrepareDynamicBlock,
  RawBlock,
  UseSectionsPreloadDataProps,
} from '~/domains/constructor-pages/types';
import { prepareDynamicBlock, kebabToPascal } from '~/helpers';
import useSectionsPreloadData from '~/domains/constructor-pages/useSectionsPreloadData';
import FormSectionPartnership from '~/components/sections/FormSection/FormSectionPartnership.vue';
import FormSectionConsultation from '~/components/sections/FormSection/FormSectionConsultation.vue';
import AppleCalculator from '~/components/AppleCalculator.vue';
import BannersSlider from '~/domains/sliders/components/BannersSlider.vue';
import SolutionsCarousel from '~/domains/sliders/components/SolutionsCarousel.vue';
import CategoryBanner from '~/domains/categories/components/CategoryBanner.vue';
import CategoryFeatures from '~/domains/categories/components/CategoryFeatures.vue';
import CategoryProductSeries from '~/domains/categories/components/CategoryProductSeries.vue';
import CategoryAccessories from '~/domains/categories/components/CategoryAccessories.vue';
import CategoryFaq from '~/domains/categories/components/CategoryFaq.vue';
import HtmlContentBlock from '~/components/HtmlContentBlock.vue';
import SoftwareCategoryTryDemo from '~/domains/categories/components/SoftwareCategoryTryDemo.vue';
import SoftwareCategoryChoosePlan from '~/domains/categories/components/SoftwareCategoryChoosePlan.vue';

const COMPONENTS_ALIASES: Record<string, string> = {
  'ui-carousel': 'feature-section-carousel',
  'ui-text-image': 'feature-section-text-image',
  'ui-video-list': 'video-section-simple',
  'ui-card-list': 'feature-section-articles',
  'categories-section': 'card-section-grid',
  'cards-section-grid': 'card-section-grid',
  'category-applications': 'card-section-apps',
  'cards-section-flags': 'card-section-flags',
  'category-info-blocks': 'feature-section-info-blocks',
  'category-recommended': 'feature-section-recommended',
  'software-category-awards': 'card-section-awards',
  'software-category-first-screen': 'feature-section-columns',
  'software-category-infoblocks': 'feature-section-simple-list',
  'software-category-reviews-slider': 'testimonial-section-slider',
  'software-category-user-help': 'document-section-simple',
  'cards-section-simple': 'card-section-simple',
  'latest-news': 'blog-section-simple',
  'catalog-header': 'header-section-image',
  'consultation-form': 'form-section-consultation',
  'partnership-form': 'form-section-partnership',
};

const EXCLUDED_BLOCKS: string[] = ['page-info', 'short_description'];
const LOCAL_SECTIONS_WRAP_DATA_PROPS = ['HtmlContentBlock'];

export default defineComponent({
  name: 'PageBlocks',

  props: {
    blocks: {
      type: Array as () => RawBlock[],
      default: () => [],
    },
  },

  setup(props) {
    const localSections = {
      AppleCalculator,
      BannersSlider,
      SolutionsCarousel,
      CategoryBanner,
      CategoryFeatures,
      CategoryProductSeries,
      CategoryAccessories,
      CategoryFaq,
      HtmlContentBlock,
      SoftwareCategoryTryDemo,
      SoftwareCategoryChoosePlan,
      FormSectionPartnership,
      FormSectionConsultation,
    };

    const asbisUiSections: Record<string, any> = {};

    forEachSections((sectionName, sectionComponent) => {
      if (!Object.keys(localSections).includes(sectionName))
        asbisUiSections[sectionName] = sectionComponent;
    });

    const sectionComponents = {
      ...localSections,
      ...asbisUiSections,
    };

    const sectionsWithDataProps = abSectionsWithDataProps.concat(
      LOCAL_SECTIONS_WRAP_DATA_PROPS,
    );

    const wrappedPrepareDynamicBlock: PrepareDynamicBlock = (block: RawBlock) =>
      prepareDynamicBlock(block, {
        excludedBlocks: EXCLUDED_BLOCKS,
        aliases: COMPONENTS_ALIASES,
        sectionsWithDataProps,
      }) as unknown as PreparedBlock;

    const { enhancedComponents } = useSectionsPreloadData(
      props as UseSectionsPreloadDataProps,
      wrappedPrepareDynamicBlock,
    );

    const getComponentToRender = (componentType?: string) =>
      componentType
        ? (sectionComponents as Record<string, any>)[
            kebabToPascal(componentType)
          ]
        : undefined;

    const getBindingData = (component: PreparedBlock) => {
      if (!component || !component.data) return {};
      return component.data;
    };

    return {
      enhancedComponents,
      getComponentToRender,
      getBindingData,
    };
  },
});
